import { faRobot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo } from 'react';

import type { AffectUser } from '@/types/affectUser';
import { type GroupEnrollment, ScheduledBySource } from '@/types/appointments';
import { Pill, Tooltip } from '@/ui';
import { formatShortDateTime } from '@/utils/dates';
import formatStaffName from '@/utils/formatStaffName';

export interface Props {
  scheduledBySource?: ScheduledBySource | null;
  initiator?: AffectUser | null;
  groupEnrollment?: GroupEnrollment | null;
}

// A group appointment's series is created by the group tooling, but the member
// is on it because a staff member added them to the group -- that is the
// scheduling decision staff look for.
const groupEnrollmentLabel = (groupEnrollment?: GroupEnrollment | null) =>
  groupEnrollment?.enrolled_by
    ? `Added to group by ${formatStaffName({ staff: groupEnrollment.enrolled_by })}`
    : 'Added to group';

const groupEnrollmentTooltip = (groupEnrollment?: GroupEnrollment | null) =>
  groupEnrollment?.enrolled_at
    ? `Added to group on ${formatShortDateTime(groupEnrollment.enrolled_at)}`
    : 'How this appointment was scheduled';

/**
 * NOTE: This component is business/domain-specific and does not belong in the
 * generic UI primitives library (ui/components). It should be moved to a more
 * appropriate domain-specific location in the codebase.
 */
const ScheduledBySourceBadge = ({ scheduledBySource, initiator, groupEnrollment }: Props) => {
  if (!scheduledBySource) return null;

  if (scheduledBySource === ScheduledBySource.GROUP_APPOINTMENT_MANAGEMENT) {
    return (
      <Tooltip content={groupEnrollmentTooltip(groupEnrollment)}>
        <Pill size="sm" color="neutral">
          {groupEnrollmentLabel(groupEnrollment)}
        </Pill>
      </Tooltip>
    );
  }

  const isSchedulingAgent = scheduledBySource === ScheduledBySource.SCHEDULING_AGENT;
  const agentLabel =
    isSchedulingAgent && initiator ? `Scheduled by ${formatStaffName({ staff: initiator })}` : null;

  return (
    <Tooltip content="How this appointment was scheduled">
      <Pill size="sm" color="neutral">
        {isSchedulingAgent ? (
          <>
            <FontAwesomeIcon icon={faRobot} className="mr-1 text-cyan-600" />
            {agentLabel ?? scheduledBySource}
          </>
        ) : (
          scheduledBySource
        )}
      </Pill>
    </Tooltip>
  );
};

export default memo(ScheduledBySourceBadge);
